import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { Link } from 'react-router-dom'

import type { EagleAvailability, EagleSparkPunto } from '../../api/types'
import { localeCorrente, t } from '../../i18n/lingua'
import { formatConUnita } from '../eagleTesti'
import { curva } from '../GraficoSerie'

/**
 * The small charts of the Eagle widgets: the ones that sit **inside a row**
 * or a tile, where there is no room for axes and a legend but the shape of
 * the data still matters — a sparkline next to a value, a bar with the
 * threshold ticks, a segmented bar of counts, the incidents on a time axis.
 *
 * Hand-written SVG and CSS like every chart of the console (`charts.tsx`,
 * `GraficoSerie.tsx`): colours from the theme tokens, real pixels measured
 * with a ResizeObserver — never a stretched viewBox, which distorts strokes
 * and text. Status colours are reserved for states and always come with a
 * word (title, aria-label), never alone.
 */

// ------------------------------------------------------------------ helpers

/** The width of a container, in real pixels, kept up to date. */
export function useLarghezza<T extends HTMLElement>(fallback = 320): [RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [larghezza, setLarghezza] = useState(fallback)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const oss = new ResizeObserver((voci) => {
      const w = voci[0]?.contentRect.width
      if (w && w > 0) setLarghezza(Math.floor(w))
    })
    oss.observe(el)
    return () => oss.disconnect()
  }, [])
  return [ref, larghezza]
}

/** The measured height of a box that fills what is left of a widget (ResizeObserver), for a chart that must take all the room it is given. */
/** Width and height of a box together (one observer): for what pages its content to fit. */
export function useRiquadro<T extends HTMLElement>(larghezzaIniziale = 600, altezzaIniziale = 400): [RefObject<T>, number, number] {
  const ref = useRef<T>(null)
  const [misure, setMisure] = useState<[number, number]>([larghezzaIniziale, altezzaIniziale])
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const oss = new ResizeObserver((voci) => {
      const r = voci[0]?.contentRect
      if (r && r.width > 0 && r.height > 0) setMisure([Math.floor(r.width), Math.floor(r.height)])
    })
    oss.observe(el)
    return () => oss.disconnect()
  }, [])
  return [ref, misure[0], misure[1]]
}

export function useAltezza<T extends HTMLElement>(fallback = 120): [RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [altezza, setAltezza] = useState(fallback)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const oss = new ResizeObserver((voci) => {
      const h = voci[0]?.contentRect.height
      if (h && h > 0) setAltezza(Math.floor(h))
    })
    oss.observe(el)
    return () => oss.disconnect()
  }, [])
  return [ref, altezza]
}

/** A "clean" maximum for an axis: 1, 2, 5 × 10ⁿ. */
export function niceMax(v: number): number {
  if (v <= 0) return 1
  const ordine = 10 ** Math.floor(Math.log10(v))
  const n = v / ordine
  const passo = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return passo * ordine
}

/** The time ticks: few, on round hours (or days for long windows). */
export function taccheTempo(t0: number, t1: number, ore: number): { ts: number; label: string }[] {
  const passo = ore <= 0.5 ? 5 * 60_000 : ore <= 1 ? 10 * 60_000 : ore <= 3 ? 30 * 60_000 : ore <= 6 ? 3_600_000 : ore <= 24 ? 3 * 3_600_000 : ore <= 48 ? 6 * 3_600_000 : 24 * 3_600_000
  const out: { ts: number; label: string }[] = []
  const prima = new Date(t0)
  prima.setSeconds(0, 0)
  const minuti = passo / 60_000
  if (passo >= 3_600_000) prima.setMinutes(0)
  else prima.setMinutes(Math.ceil(prima.getMinutes() / minuti) * minuti)
  if (passo >= 24 * 3_600_000) prima.setHours(0)
  else if (passo > 3_600_000) prima.setHours(Math.ceil(prima.getHours() / (passo / 3_600_000)) * (passo / 3_600_000))
  for (let ts = prima.getTime(); ts <= t1; ts += passo) {
    if (ts < t0) continue
    const d = new Date(ts)
    out.push({
      ts,
      label: passo >= 24 * 3_600_000
        ? d.toLocaleDateString(localeCorrente(), { day: '2-digit', month: '2-digit' })
        : d.toLocaleTimeString(localeCorrente(), { hour: '2-digit', minute: '2-digit' }),
    })
  }
  return out
}

export function formatOra(ts: number): string {
  return new Date(ts).toLocaleTimeString(localeCorrente(), { hour: '2-digit', minute: '2-digit' })
}

export function formatIstante(ts: number): string {
  return new Date(ts).toLocaleString(localeCorrente(), { dateStyle: 'short', timeStyle: 'short' })
}

/** A duration in words: seconds, minutes, or hours and minutes. */
export function durataBreve(sec: number): string {
  return sec < 60 ? t('{n} s', { n: Math.round(sec) })
    : sec < 3600 ? t('{n} min', { n: Math.round(sec / 60) })
      : t('{h} h {m} min', { h: Math.floor(sec / 3600), m: Math.round((sec % 3600) / 60) })
}

/** The typical gap between two readings (the median): it is how gaps are told apart. */
function passoTipico(tempi: number[]): number {
  const diff: number[] = []
  for (let i = 1; i < tempi.length; i++) diff.push(tempi[i] - tempi[i - 1])
  if (!diff.length) return 60_000
  diff.sort((a, b) => a - b)
  return Math.max(30_000, diff[Math.floor(diff.length / 2)])
}

const numero = (v: number, unit = '') => formatConUnita(v, unit)

export type Tono = 'ok' | 'warn' | 'error' | 'muted'

// ---------------------------------------------------------------- sparkline

/**
 * The last hours of a value, in a strip a few pixels tall: the line is the
 * mean of every interval, the faint band its minimum–maximum (the peaks the
 * mean hides), the dot the last reading in the colour of its state. The
 * thresholds are drawn when they fall inside the scale. Gaps stay gaps.
 */
export function Sparkline({
  punti, ore, unit = '', warn = null, crit = null, altezza = 30, tono = 'muted', nome = '',
}: {
  punti: EagleSparkPunto[]
  ore: number
  unit?: string
  warn?: number | null
  crit?: number | null
  altezza?: number
  tono?: Tono
  nome?: string
}) {
  const [ref, larghezza] = useLarghezza<HTMLDivElement>(120)
  const idVelo = useId()
  const adesso = useMemo(() => Date.now(), [punti])
  const validi = useMemo(() => punti.filter((p) => p.value != null)
    .map((p) => ({ ...p, t: new Date(p.ts).getTime() })).sort((a, b) => a.t - b.t), [punti])
  if (validi.length < 2) {
    return <div ref={ref} className="gspark gspark--vuota" style={{ height: altezza }}><span>{t('poche misure')}</span></div>
  }
  const t0 = adesso - ore * 3_600_000
  const t1 = adesso
  const W = Math.max(60, larghezza)
  const H = altezza
  const M = { top: 3, right: 4, bottom: 3, left: 1 }
  const plotW = W - M.left - M.right
  const plotH = H - M.top - M.bottom
  const valori = validi.flatMap((p) => [p.value, p.min, p.max]).filter((v): v is number => v != null)
  const soglie = [warn, crit].filter((v): v is number => v != null)
  let massimo = niceMax(Math.max(1, ...valori, ...soglie))
  if (unit === '%' && massimo <= 100) massimo = 100
  const x = (ts: number) => M.left + ((ts - t0) / Math.max(1, t1 - t0)) * plotW
  const y = (v: number) => M.top + plotH - (Math.min(v, massimo) / massimo) * plotH
  const passo = passoTipico(validi.map((p) => p.t))

  const tratti: typeof validi[] = []
  let corrente: typeof validi = []
  let prima = 0
  for (const p of validi) {
    if (corrente.length && p.t - prima > passo * 3) {
      tratti.push(corrente)
      corrente = []
    }
    corrente.push(p)
    prima = p.t
  }
  if (corrente.length) tratti.push(corrente)
  // La stessa curva morbida (monotona) del grafico grande, e il velo sotto.
  const xy = (tr: typeof validi, prendi: (p: typeof validi[number]) => number) => tr.map((p) => ({ x: x(p.t), y: y(prendi(p)) }))
  const linea = tratti.map((tr) => curva(xy(tr, (p) => p.value as number))).join(' ')
  const baseY = M.top + plotH
  const velo = tratti.map((tr) => {
    const sopra = xy(tr, (p) => p.value as number)
    return `${curva(sopra)} L ${sopra[sopra.length - 1].x.toFixed(1)} ${baseY.toFixed(1)} L ${sopra[0].x.toFixed(1)} ${baseY.toFixed(1)} Z`
  }).join(' ')
  const fascia = tratti
    .filter((tr) => tr.some((p) => p.min != null && p.max != null))
    .map((tr) => `${curva(xy(tr, (p) => p.max ?? (p.value as number)))} ${curva([...xy(tr, (p) => p.min ?? (p.value as number))].reverse()).replace(/^M/, 'L')} Z`)
    .join(' ')
  const ultimo = validi[validi.length - 1]
  const minimo = Math.min(...valori)
  const picco = Math.max(...valori)
  const descrizione = t('{nome}: ultime {ore} ore, minimo {min}, massimo {max}, ultimo {ultimo}', {
    nome: nome || t('andamento'), ore, min: numero(minimo, unit), max: numero(picco, unit), ultimo: numero(ultimo.value as number, unit),
  })

  return (
    <div ref={ref} className="gspark" style={{ height: altezza }} title={descrizione}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="gspark__svg" role="img" aria-label={descrizione}>
        <defs>
          <linearGradient id={`${idVelo}-velo`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.3} />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        {warn != null && warn <= massimo && <line x1={M.left} x2={W - M.right} y1={y(warn)} y2={y(warn)} className="gs__soglia gs__soglia--warn" />}
        {crit != null && crit <= massimo && <line x1={M.left} x2={W - M.right} y1={y(crit)} y2={y(crit)} className="gs__soglia gs__soglia--crit" />}
        <path d={velo} fill={`url(#${idVelo}-velo)`} className="gspark__velo" />
        {fascia && <path d={fascia} className="gspark__fascia" />}
        <path d={linea} className="gspark__linea" />
        <circle cx={x(ultimo.t)} cy={y(ultimo.value as number)} r={3} className={`gspark__ultimo gspark__ultimo--${tono}`} />
      </svg>
    </div>
  )
}

// ------------------------------------------------------------ bar with ticks

/**
 * A value as a bar on a fixed scale, with the warning and critical thresholds
 * as ticks on the track: where the value stands, and how far from trouble,
 * in one glance. The fill takes the colour of the state only when the state
 * is not ok — a column of amber and red bars would shout for nothing.
 */
export function BarraSoglie({
  valore, massimo, warn = null, crit = null, traguardo = null, tono = 'muted', unit = '', altezza = 8, testo, offset = 0,
}: {
  valore: number
  massimo: number
  warn?: number | null
  crit?: number | null
  /** A target to reach (an SLA), as a tick in the accent colour. */
  traguardo?: number | null
  tono?: Tono
  unit?: string
  altezza?: number
  /** The words for the bar, when value and scale are not the ones to read aloud (a shifted scale). */
  testo?: string
  /** On a shifted scale (availability from 90 to 100 %), what to add back to the numbers in the words. */
  offset?: number
}) {
  const m = Math.max(1, massimo)
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / m) * 100)).toFixed(2)}%`
  const vero = (v: number) => numero(v + offset, unit)
  const parole = [
    warn != null ? t('attenzione da {v}', { v: vero(warn) }) : '',
    crit != null ? t('critico da {v}', { v: vero(crit) }) : '',
    traguardo != null ? t('traguardo {v}', { v: vero(traguardo) }) : '',
  ].filter(Boolean).join(' · ')
  return (
    <span className={`gsoglie gsoglie--${tono}`} style={{ height: altezza }} role="img"
          aria-label={testo ?? `${vero(valore)} ${t('su {max}', { max: vero(m) })}${parole ? ` · ${parole}` : ''}`}>
      <span className="gsoglie__pieno" style={{ width: pct(valore) }} />
      {warn != null && warn <= m && <span className="gsoglie__tacca gsoglie__tacca--warn" style={{ left: pct(warn) }} title={t('attenzione da {v}', { v: vero(warn) })} />}
      {crit != null && crit <= m && <span className="gsoglie__tacca gsoglie__tacca--crit" style={{ left: pct(crit) }} title={t('critico da {v}', { v: vero(crit) })} />}
      {traguardo != null && traguardo <= m && <span className="gsoglie__tacca gsoglie__tacca--target" style={{ left: pct(traguardo) }} title={t('traguardo {v}', { v: vero(traguardo) })} />}
    </span>
  )
}

// ---------------------------------------------------------------- segments

export interface Segmento {
  key: string
  label: string
  value: number
  /** `crit` is the error colour at lower opacity, as in the alarm chart: next to `error` (down) it still reads as two. */
  tone: Tono | 'info' | 'crit'
}

/**
 * Counts as one bar divided into segments, 2px of surface between them so
 * the boundary shows even when the colours do not help. Compact: the words
 * live in the title and the aria-label, the legend is the caller's.
 */
export function Segmenti({ parti, altezza = 8 }: { parti: Segmento[]; altezza?: number }) {
  const piene = parti.filter((p) => p.value > 0)
  const totale = piene.reduce((s, p) => s + p.value, 0)
  const parole = piene.map((p) => `${p.value.toLocaleString(localeCorrente())} ${p.label}`).join(', ')
  if (!totale) return <span className="gseg gseg--vuota" style={{ height: altezza }} aria-hidden="true" />
  return (
    <span className="gseg" style={{ height: altezza }} role="img" aria-label={parole} title={parole}>
      {piene.map((p) => (
        <span key={p.key} className={`gseg__parte gseg__parte--${p.tone}`} style={{ flexGrow: p.value }} />
      ))}
    </span>
  )
}

/** The counts of a CI's parameters by state, as segments: ok, warning, critical, down, waiting. */
export function segmentiDaConteggi(c: { ok: number; warn: number; crit: number; down: number; nodata: number; pending: number }): Segmento[] {
  return [
    { key: 'ok', label: t('ok'), value: c.ok, tone: 'ok' },
    { key: 'warn', label: t('attenzione'), value: c.warn, tone: 'warn' },
    { key: 'crit', label: t('critico'), value: c.crit, tone: 'crit' },
    { key: 'down', label: t('non risponde'), value: c.down, tone: 'error' },
    { key: 'pending', label: t('in attesa'), value: c.pending + c.nodata, tone: 'muted' },
  ]
}

// ----------------------------------------------------------------- incidents

type Incidente = EagleAvailability['incidents'][number]

/**
 * The incidents on a time axis, one row per CI: when each outage started and
 * how long it lasted, side by side — a list of durations does not show that
 * three CIs went down together at 03:10. Rows are the CIs with the most
 * recent incident first; a row's name opens the CI.
 */
export function TimelineIncidenti({ incidenti, ore, maxRighe = 8 }: { incidenti: Incidente[]; ore: number; maxRighe?: number }) {
  const [ref, larghezza] = useLarghezza<HTMLDivElement>(320)
  const adesso = useMemo(() => Date.now(), [incidenti])
  const righe = useMemo(() => {
    const per = new Map<string, { nome: string; voci: Incidente[]; ultimo: number }>()
    for (const i of incidenti) {
      const da = new Date(i.from).getTime()
      const v = per.get(i.ci_id) ?? { nome: i.ci_name, voci: [], ultimo: 0 }
      v.voci.push(i)
      v.ultimo = Math.max(v.ultimo, da)
      per.set(i.ci_id, v)
    }
    return [...per.entries()].sort((a, b) => b[1].ultimo - a[1].ultimo).slice(0, maxRighe)
  }, [incidenti, maxRighe])
  if (!righe.length) return null
  const t0 = adesso - ore * 3_600_000
  const t1 = adesso
  const W = Math.max(120, larghezza)
  const H = 20
  const x = (ts: number) => Math.max(0, Math.min(W, ((ts - t0) / Math.max(1, t1 - t0)) * W))
  const tempi = taccheTempo(t0, t1, ore)

  return (
    <div className="gincidenti">
      <div className="gincidenti__nomi">
        {righe.map(([id, r]) => (
          <Link key={id} to={`/cis/${id}#eagle`} className="gincidenti__nome" title={r.nome}>{r.nome}</Link>
        ))}
        <span className="gincidenti__nome gincidenti__nome--asse" aria-hidden="true" />
      </div>
      <div className="gincidenti__strisce" ref={ref}>
        {righe.map(([id, r]) => (
          <svg key={id} width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="gincidenti__striscia" role="img"
               aria-label={t('{nome}: {n} incidenti', { nome: r.nome, n: r.voci.length })}>
            <line x1={0} x2={W} y1={H / 2} y2={H / 2} className="gincidenti__base" />
            {r.voci.map((v, i) => {
              const a = x(new Date(v.from).getTime())
              const b = Math.max(a + 2, x(new Date(v.to).getTime()))
              return (
                <rect key={`${v.from}|${i}`} x={a} y={5} width={b - a} height={10} rx={2} className="gincidenti__tratto">
                  <title>{`${formatIstante(new Date(v.from).getTime())} – ${formatOra(new Date(v.to).getTime())} · ${durataBreve(v.duration_s)}`}</title>
                </rect>
              )
            })}
          </svg>
        ))}
        <svg width={W} height={16} viewBox={`0 0 ${W} 16`} className="gincidenti__asse" aria-hidden="true">
          {tempi.map((tt) => (
            <g key={tt.ts}>
              <line x1={x(tt.ts)} x2={x(tt.ts)} y1={0} y2={4} className="gs__grid" />
              <text x={x(tt.ts)} y={14} textAnchor="middle" className="gs__tick">{tt.label}</text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  )
}
